import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {Grid, GridCol, Input} from '@flixbus/honeycomb-react';
import InputWithSelection from '@/components/InputWithSelection/InputWithSelection';
import {SelectOption} from '@/components/InputWithSelection/InputWithSelection.types';
import {RegionalCostsVehicleCost} from '@/Pages/RegionalCosts/store/types';
import ContentLoader from '@/Pages/Common/ContentLoader';
import CpcPanel from '@/Pages/Common/CpcPanel';
import {validateInputNumber} from '@/Pages/Common/Utils';
import {useLocalUnitsContext} from '@/Pages/Common/LocalUnits';
import {
  getValueDimensions,
  ValueDimension
} from '@/Pages/Countries/components/index.constants';
import {
  VehicleCostsPanelData,
  VehicleCostsPanelProps
} from './VehicleCostsPanel.types';

type PanelInputFieldConfig = {
  key: keyof RegionalCostsVehicleCost;
  name: string;
  unit: string;
  integerDigitsCount?: number;
  fractionDigitsCount?: number;
  mandatory?: boolean;
};

type PanelInputWithSelectionFieldConfig = PanelInputFieldConfig & {
  options: SelectOption[];
};

const VehicleCostsPanel: React.FC<VehicleCostsPanelProps> = ({
  country,
  year,
  panelData,
  config,
  updateFn,
  onClose
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [localData, setLocalData] = useState<VehicleCostsPanelData | null>(
    null
  );

  const {localUnits} = useLocalUnitsContext();

  useEffect(() => {
    setLocalData(panelData);
  }, [panelData]);

  const fields: Array<
    PanelInputFieldConfig | PanelInputWithSelectionFieldConfig
  > = useMemo(() => {
    return config.map(opt => {
      const isInput = ![
        'repairCostPerMonth',
        'repairCostPerDistanceUnit'
      ].includes(opt.key);
      if (isInput) return opt as PanelInputFieldConfig;

      return {
        ...opt,
        options: getValueDimensions(country.currency, localUnits)
      } as PanelInputWithSelectionFieldConfig;
    });
  }, [config, country.currency, localUnits]);

  const onChangeInputWithSelection = useCallback(
    (value: string, field: keyof VehicleCostsPanelData) => {
      if (field === 'repairCostUnit') {
        // selector was changed
        setLocalData(
          prevData =>
            ({
              ...prevData,
              [field]: value ?? null
            } as VehicleCostsPanelData)
        );
        return;
      } else {
        // input was changed
        const fieldConfig = config.find(
          c =>
            c.key === 'repairCostPerMonth' ||
            c.key === 'repairCostPerDistanceUnit'
        );

        const newValue = validateInputNumber(
          value,
          String(localData?.[field]),
          fieldConfig?.integerDigitsCount,
          fieldConfig?.fractionDigitsCount
        );

        setLocalData(
          prevData =>
            ({
              ...prevData,
              [field]: newValue ?? null
            } as VehicleCostsPanelData)
        );
      }
    },
    [config, localData]
  );

  const onChangeInput = useCallback(
    (value: string, fieldConfig: PanelInputFieldConfig) => {
      const newValue = validateInputNumber(
        value,
        String(localData?.[fieldConfig.key]),
        fieldConfig?.integerDigitsCount,
        fieldConfig?.fractionDigitsCount
      );

      setLocalData(
        prevData =>
          ({
            ...prevData,
            [fieldConfig.key]: newValue ?? null
          } as VehicleCostsPanelData)
      );
    },
    [localData]
  );

  const inputRenderer = useCallback(
    (field: PanelInputFieldConfig, localData: VehicleCostsPanelData) => (
      <Input
        key={`vehicle-cost-panel-${field.key}`}
        name={`vehicle-cost-panel-${field.key}`}
        id={`vehicle-cost-panel-${field.key}`}
        extraClasses="vehicle-cost-panel-input"
        type="number"
        step="0.1"
        label={`${field.name}${field.mandatory ? '*' : ''}`}
        inlineLabelLeft={field.unit}
        value={localData[field.key] ?? ''}
        onChange={event => onChangeInput(event.target.value, field)}
      />
    ),
    [onChangeInput]
  );

  const inputWithSelectionRenderer = useCallback(
    (
      field: PanelInputWithSelectionFieldConfig,
      localData: VehicleCostsPanelData
    ) => (
      <InputWithSelection
        selectorOptions={field.options}
        selectorValue={localData.repairCostUnit ?? ''}
        inputValue={localData.repairCostValue?.toString() ?? ''}
        label={field.name}
        onChange={({inputValue, selectorValue}) => {
          if (selectorValue !== localData.repairCostUnit) {
            onChangeInputWithSelection(selectorValue, 'repairCostUnit');
          }

          if (inputValue !== String(localData.repairCostValue)) {
            onChangeInputWithSelection(inputValue, 'repairCostValue');
          }
        }}
      />
    ),
    [onChangeInputWithSelection]
  );

  const fieldsRendered = useCallback(() => {
    if (!localData) return null;

    return fields.map(field => (
      <GridCol key={field.key} size={12}>
        {['repairCostPerMonth', 'repairCostPerDistanceUnit'].includes(field.key)
          ? inputWithSelectionRenderer(
              field as PanelInputWithSelectionFieldConfig,
              localData
            )
          : inputRenderer(field, localData)}
      </GridCol>
    ));
  }, [fields, inputRenderer, inputWithSelectionRenderer, localData]);

  const convertPanelDataToVehicleCosts = useCallback(
    (panelData: VehicleCostsPanelData): RegionalCostsVehicleCost => ({
      adblueCost: panelData.adblueCost,
      cleaningCost: panelData.cleaningCost,
      currency: country.currency,
      dieselCost: panelData.dieselCost,
      flixStandardCost: panelData.flixStandardCost,
      legalCost: panelData.legalCost,
      lngCost: panelData.lngCost,
      otherCost: panelData.otherCost,
      overheadCost: panelData.overheadCost,
      repairCostPerMonth:
        panelData.repairCostUnit === ValueDimension.PER_MONTH
          ? panelData.repairCostValue
          : null,
      repairCostPerDistanceUnit:
        panelData.repairCostUnit === ValueDimension.PER_DISTANCE_UNIT
          ? panelData.repairCostValue
          : null,
      tiresCost: panelData.tiresCost,
      year
    }),
    [country.currency, year]
  );

  const onSuccess = useCallback(async () => {
    if (!localData) return;

    setLoading(true);

    const updatedCosts = convertPanelDataToVehicleCosts(localData);
    await updateFn(updatedCosts);

    setLoading(false);
    onClose();
  }, [convertPanelDataToVehicleCosts, localData, onClose, updateFn]);

  return (
    <CpcPanel
      title={`Update vehicle cost for ${country.name}`}
      activeAction={!!localData}
      closeAction={onClose}
      saveAction={onSuccess}
      extraClasses="vehicle-cost-panel"
      loading={loading}
    >
      {localData ? (
        <Grid extraClasses="gap-2">{fieldsRendered()}</Grid>
      ) : (
        <ContentLoader />
      )}
    </CpcPanel>
  );
};

export default React.memo(VehicleCostsPanel);
